/**
 * TarifForm — tarif **create** surface (`/pujcovna/sprava/tarify/novy`). Create is one full page
 * (AGENTS.md editing pattern); editing is `TarifEditModal`; the form body is shared. Submits through
 * the form id (Enter + native validation). `pujcovna:admin`, like the API.
 */

import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { FormPanel, SectionHeader } from '@/components/FormPanel';
import { Icon } from '@/components/Icon';
import { Savebar } from '@/components/Savebar';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useModuleAccess } from '@/lib/permissions';
import { useCreateTarifMutation } from './api';
import { TARIF_FORM_ID, TarifFormBody, tarifSeed, tarifToBody, type TarifValue } from './tarif-section-forms';

export function TarifForm() {
  const navigate = useNavigate();
  const { can, resolved } = useModuleAccess();
  const create = useCreateTarifMutation();
  const saving = create.isPending;

  const seed = useMemo(() => tarifSeed(), []);
  const [nazev, setNazev] = useState('');

  const back = () => navigate({ to: '/pujcovna/sprava', search: { nastroj: 'tarify' } });

  const submit = (form: TarifValue) => {
    if (!form.nazev.trim()) {
      toast.error('Zadejte název tarifu.');
      return;
    }
    const body = tarifToBody(form);
    if (!body.ok) {
      toast.error(body.chyba);
      return;
    }
    create.mutate(body.body, {
      onSuccess: () => {
        toast.ok('Tarif vytvořen.');
        back();
      },
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se vytvořit tarif.'}`),
    });
  };

  const denied = resolved && !can('pujcovna', 'admin');

  return (
    <Shell rail={<AppRail active="pujcovna" />}>
      <main className="flex min-w-0 flex-col overflow-auto">
        <Crown>
          <Breadcrumb
            segments={[
              { label: 'Půjčovna', href: '/pujcovna' },
              { label: 'Správa', href: '/pujcovna/sprava?nastroj=tarify' },
              { label: 'Nový tarif' },
            ]}
          />
          <Crown.Row className="mt-3">
            <DisplayHeading>
              Nový <em>tarif.</em>
            </DisplayHeading>
          </Crown.Row>
        </Crown>

        {!resolved ? (
          <div className="px-8 pt-5">
            <Skeleton className="h-24 w-full" />
          </div>
        ) : denied ? (
          <div data-testid="tarif-form-denied" className="px-8 pb-8 pt-5">
            <EmptyState
              eyebrow="403"
              title="Tarify půjčovny zakládá správce půjčovny."
              description="Pro přístup požádejte správce firmy."
            />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-6 px-8 py-8">
              <FormPanel>
                <SectionHeader
                  number={1}
                  title={<>Název <em>a podmínky</em>.</>}
                  description="Tarif určuje cenu vozidel půjčovny: marži nad režijní cenu, pásma délky pronájmu a sezóny. Ceník lze doplnit i později."
                />
                <TarifFormBody
                  initial={seed}
                  busy={saving}
                  onSubmit={submit}
                  onChange={(v) => setNazev((n) => (n === v.nazev ? n : v.nazev))}
                />
              </FormPanel>
            </div>

            <Savebar
              meta={[
                { k: 'Režim', v: 'Nový záznam' },
                { k: 'Tarif', v: nazev || '—' },
              ]}
              actions={
                <>
                  <Button variant="secondary" icon={<Icon name="chevronL" size={12} />} onClick={back} disabled={saving}>
                    Zrušit
                  </Button>
                  <Button variant="ink" type="submit" form={TARIF_FORM_ID} disabled={saving}>
                    Vytvořit tarif
                  </Button>
                </>
              }
            />
          </>
        )}
      </main>
    </Shell>
  );
}
